import WMIcon from '../ui/WMIcon';

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-700 dark:text-amber-300' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-700 dark:text-blue-300' },
  done:        { label: 'Done',        bg: 'bg-green-100 dark:bg-green-900/30',    text: 'text-green-700 dark:text-green-300' },
  on_hold:     { label: 'On Hold',     bg: 'bg-gray-100 dark:bg-gray-700/40',     text: 'text-gray-600 dark:text-gray-300' },
  cancelled:   { label: 'Cancelled',   bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-700 dark:text-red-300' },
};

const TECH_COLORS = ['#2E8BF0', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899'];

function techColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return TECH_COLORS[Math.abs(hash) % TECH_COLORS.length];
}

function initials(name) {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function WorkOrderCard({ workOrder, onClick }) {
  const { vehicleLabel, customerName, assignedTo = [], steps = [], status, scheduledStart, estimateId, id } = workOrder;

  const doneCount = steps.filter(s => s.done).length;
  const totalCount = steps.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const st = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  const dateLabel = scheduledStart
    ? new Date(scheduledStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <button
      onClick={() => onClick?.(workOrder)}
      className="w-full text-left bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-xl p-3.5 hover:border-[#2E8BF0] dark:hover:border-[#2E8BF0] hover:shadow-md transition-all cursor-pointer group"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate leading-tight">{vehicleLabel}</p>
          <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] truncate mt-0.5">{customerName}</p>
        </div>
        <span className={`flex-shrink-0 text-[var(--wm-text-caption)] font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
          {st.label}
        </span>
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="mb-2.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">Steps</span>
            <span className="text-[var(--wm-text-caption)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{doneCount}/{totalCount}</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden">
            <div
              className="h-full rounded-full bg-[#2E8BF0] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer row */}
      <div className="flex items-center justify-between gap-2">
        {/* Tech avatars */}
        <div className="flex items-center -space-x-1.5">
          {assignedTo.slice(0, 4).map((tech) => {
            const color = techColor(tech);
            return (
              <div
                key={tech}
                title={tech}
                className="w-5 h-5 rounded-full border-2 border-white dark:border-[#1B2A3E] flex items-center justify-center text-[var(--wm-text-micro)] font-bold flex-shrink-0"
                style={{ backgroundColor: `${color}22`, color, borderColor: undefined }}
              >
                {initials(tech)}
              </div>
            );
          })}
          {assignedTo.length > 4 && (
            <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-[#243348] border-2 border-white dark:border-[#1B2A3E] flex items-center justify-center text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE] font-semibold">
              +{assignedTo.length - 4}
            </div>
          )}
          {assignedTo.length === 0 && (
            <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] italic">Unassigned</span>
          )}
        </div>

        {/* Right side: date + estimate link */}
        <div className="flex items-center gap-2">
          {dateLabel && (
            <span className="flex items-center gap-1 text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
              <WMIcon name="calendar" className="w-3 h-3" />
              {dateLabel}
            </span>
          )}
          {estimateId && (
            <span className="text-[var(--wm-text-caption)] text-[#2E8BF0] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              #{estimateId}
            </span>
          )}
        </div>
      </div>

      {/* WO ID chip */}
      <div className="mt-2 text-[var(--wm-text-micro)] font-mono text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wider">
        WO-{id.replace('wo-', '').toUpperCase()}
      </div>
    </button>
  );
}
